import { Component, EventEmitter, Input, NgZone, Optional, Output } from '@angular/core';

import {
    catchError, combineLatest, concatMap, debounceTime, delay, filter, firstValueFrom, from, map, Observable, of, Subscription, switchMap, take,
    tap, throwError
} from 'rxjs';

import { Am5ChartComponent } from '@Common/Charts/components/Am5Chart/am5-chart.component';
import { BriefingChartDisplayElementModel } from '@Common/PageBuilder/models/briefing-chart-display-element.model';
import { ReportsSeriesDataMergerService } from '@Common/Reports/services/reports-series-data-merger.service';
import { ReportsSeriesDataGrouperService } from '@Common/Reports/services/reports-series-data-grouper.service';
import { Am5Chart } from '@Common/Charts/models/am5-chart.model';
import { ChartDisplayElementModel } from '@Common/Reports/models/chart-display-element.model';
import { LocalizationService } from '@Core/Localization/services/localization.service';
import { ChartSpecificTypesEnum } from '@Common/Reports/enums/chart-specific-types.enum';
import { ChartAnnotationIndicatorsService } from '@Common/Dialogs/components/ChartAnnotationDialog/chart-annotation-indicators.service';


@Component({
    selector: 'agpr-am5-briefing-chart',
    templateUrl: './am5-briefing-chart.component.html',
    styleUrls: ['./am5-briefing-chart.component.scss'],
    standalone: false
})
export class Am5BriefingChartComponent extends Am5ChartComponent {

    @Input() briefingChartDisplayElement: BriefingChartDisplayElementModel;
    @Input() imageWidth: number;
    @Input() generateOnSend: boolean;
    @Input() twigTemplate: string = '';
    @Input() twigData: any = {};
    @Input() onExportResized?: (exportWidth: number, exportHeight: number) => void | Promise<void>;

    containerWidth: number = 640;
    containerHeight: number = 250;
    watchDataSub: Subscription;

    // dance and can corrupt each other's capture.
    private exportChain: Promise<void> = Promise.resolve();
    private annotationBaked = false;

    @Output() sourceDeleted: EventEmitter<boolean> = new EventEmitter(false);
    @Output() renderedOutputGenerated: EventEmitter<string> = new EventEmitter();
    @Output() imageGenerated: EventEmitter<string> = new EventEmitter();


    constructor(
        public reportsSeriesDataMergerService: ReportsSeriesDataMergerService,
        public localizationService: LocalizationService,
        public chartAnnotationIndicatorsService: ChartAnnotationIndicatorsService,
        public zone: NgZone,
        @Optional() public reportsSeriesDataGrouperService?: ReportsSeriesDataGrouperService
    ) {
        super(reportsSeriesDataMergerService, chartAnnotationIndicatorsService, zone, reportsSeriesDataGrouperService);
    }

    ngOnDestroy(): void {
        super.ngOnDestroy();
        if (this.watchDataSub) {
            this.watchDataSub.unsubscribe();
            this.watchDataSub = null;
        }
    }

    initializeChart() {
        if (!this.viewInitted) {
            return;
        }

        this.series = (this.briefingChartDisplayElement.chartBlock.displayElement as ChartDisplayElementModel).series;
        cancelAnimationFrame(this.initFrame);
        this.initFrame = requestAnimationFrame(() => {
            this.containerWidth = this.imageWidth;
            this.containerHeight = this.briefingChartDisplayElement.chart.heightPx;
            if (this.chart) {
                this.zone.runOutsideAngular(() => {
                    this.chart.initChart(this.chartElement.nativeElement, this.chart.chartType);
                    this.generateImage();
                });
            }
        });
    }

    generateImage(): void {
        if (this.watchDataSub) {
            this.watchDataSub.unsubscribe();
            this.watchDataSub = null;
        }

        this.watchDataSub = this.watchData(this.chart).pipe(
            debounceTime(300),
            concatMap(() => from(this.queueExport(false)))
        ).subscribe();
    }

    async regenerateImageForAnnotationChange(): Promise<void> {
        await this.queueExport(true);
    }

    private queueExport(force: boolean): Promise<void> {
        const run = this.exportChain.then(() => this.exportImageForData(force));
        this.exportChain = run.catch(() => undefined);
        return run;
    }

    private async ensureAnnotationBaked(): Promise<void> {
        if (this.annotationBaked) {
            return;
        }
        this.annotationBaked = true;
        const annotator = this.chart.annotator;
        const markerState = this.chart.settings.chartAnnotations?.markerState;
        if (!annotator || !markerState) {
            return;
        }
        await annotator.getMarkerArea();
        if (annotator.get('markerState') !== markerState) {
            annotator.set('markerState', markerState);
        }
        await annotator.waitForBake();
    }

    private async exportImageForData(force: boolean = false): Promise<void> {
        if (!this.chart.amchart) {
            return;
        }
        this.chart.root.container.setAll({
            paddingTop: 0,
            paddingRight: 0,
            paddingBottom: 0,
            paddingLeft: 0
        });
        if (this.chart.legend) {
            this.chart.legend.setAll({
                paddingTop: 0,
                paddingRight: 0,
                paddingBottom: 0,
                paddingLeft: 0
            });
        }

        let waitTime = 0;
        const displayElement = this.briefingChartDisplayElement;
        const chartSpecificType = (this.briefingChartDisplayElement.chartBlock.displayElement as ChartDisplayElementModel).chartSpecificType;
        if (chartSpecificType === ChartSpecificTypesEnum.WORD_CLOUD) {
            // wait for 800 ms to export the image as for word cloud, the animation takes some  time.
            waitTime = 800;
        }

        let width = this.imageWidth;
        this.briefingChartDisplayElement.chart.widthPx = this.imageWidth;
        const oldChartUrl = displayElement.chart.chartUrl || displayElement.chart.oldChartUrl || null;
        let height = displayElement.chart.heightPx || null;
        let oldWidth = displayElement.chart.oldWidthPx || null;
        let oldHeight = displayElement.chart.oldHeightPx || null;

        // BE saves doubles instead of integers, so it will always be off when we set the width %
        if (width) {
            width = Math.floor(width);
        }
        if (height) {
            height = Math.floor(height);
        }
        if (oldWidth) {
            oldWidth = Math.floor(oldWidth);
        }
        if (oldHeight) {
            oldHeight = Math.floor(oldHeight);
        }

        if (force || (oldWidth !== width) || (oldHeight !== height) || displayElement.chart.generateOnSend || displayElement.chart.refreshed ||
            !displayElement.chartBlock.equals(displayElement.oldChartBlock)
        ) {
            displayElement.chart.oldWidthPx = width;
            displayElement.chart.oldHeightPx = height;
            const annotator = this.chart.annotator;
            const preExportGeometry = annotator ? {
                containerWidth: annotator.containerWidth,
                containerHeight: annotator.containerHeight,
                plotTop: annotator.plotTop,
                plotBottom: annotator.plotBottom,
                plotLeft: annotator.plotLeft
            } : null;
            await this.ensureAnnotationBaked();
            const imageString = await firstValueFrom(this.waitForVisible(this.chartElement.nativeElement).pipe(
                delay(waitTime),
                switchMap(() => from(this.chart.getPNGImageData(
                    width,
                    height ?? undefined,
                    true, // transparent - lets the email block's own background color show through
                    true, // keepFontsize
                    this.onExportResized,
                    () => {
                        if (annotator && preExportGeometry?.containerWidth && preExportGeometry?.containerHeight) {
                            annotator.setContainerWidth(preExportGeometry.containerWidth);
                            annotator.setContainerHeight(preExportGeometry.containerHeight);
                            annotator.setPlotTop(preExportGeometry.plotTop);
                            annotator.setPlotBottom(preExportGeometry.plotBottom);
                            annotator.setPlotLeft(preExportGeometry.plotLeft);
                            annotator.applyContainerGeometry();
                        }
                    }
                )))
            ));
            this.briefingChartDisplayElement.chart.refreshed = false;
            this.imageGenerated.emit(imageString);
            this.watchLoading();
        } else {
            this.imageGenerated.emit(oldChartUrl);
            this.watchLoading();
        }
    }

    /**
     * amCharts' PNG export needs the container to have real layout dimensions. When the chart lives in an
     * inactive briefing step (kept in the DOM but hidden via display:none), it has none, so wait for it to
     * become visible - e.g. when the user switches to that step - before exporting.
     */
    waitForVisible(element: HTMLElement): Observable<void> {
        if (element.getClientRects().length > 0) {
            return of(undefined);
        }
        return new Observable<void>(subscriber => {
            const observer = new ResizeObserver(() => {
                if (element.getClientRects().length > 0) {
                    subscriber.next();
                    subscriber.complete();
                }
            });
            observer.observe(element);
            return () => observer.disconnect();
        }).pipe(take(1));
    }

    watchData(chart: Am5Chart) {
        const series = (this.briefingChartDisplayElement.chartBlock.displayElement as ChartDisplayElementModel).series;
        const data = series.map(item => null);
        const previousData = series.map(item => null);
        const erroredSeries = series.map(() => false);

        const series$ = series.map((item, index) => {
            const previousPeriodDataProvider$ = (item.hasPreviousPeriod) ? item.previousPeriodDataProvider
                .pipe(
                    filter(v => !!v),
                    map((itemData) => {
                        this.zone.runOutsideAngular(() => {
                            previousData[index] = itemData;
                            return of(previousData);
                        });
                    }),
                    catchError(err => {
                        this.zone.runOutsideAngular(() => {
                            previousData[index] = [];
                            erroredSeries[index] = true;
                            if (!erroredSeries.includes(false)) {
                                this.noData.emit(true);
                            }
                        });
                        return throwError(() => new Error(err));
                    })
                ) : of([]);
            const currentPeriodDataProvider$ = item.dataProvider.pipe(
                filter(v => !!v),
                map(itemData => {
                    this.zone.runOutsideAngular(() => {
                        data[index] = itemData;
                        if (chart.overrideNoContent) {
                            // sometimes this is needed because there is no previousDataSeries but existin in useNewDataMappingWorkflow
                            if (chart.isContentEmpty && chart.isContentEmpty()) {
                                this.noContent.emit(true);
                            } else {
                                this.noContent.emit(false);
                            }
                        }
                        return of(data);
                    });
                }),
                catchError(err => {
                    this.zone.runOutsideAngular(() => {
                        data[index] = [];
                        erroredSeries[index] = true;
                        if (!erroredSeries.includes(false)) {
                            this.noData.emit(true);
                        }
                        if (err.message === this.localizationService.transform('Briefings.error.chart.dataNotAvailable')) {
                            this.sourceDeleted.emit(true);
                        }
                    });
                    return throwError(() => new Error(err));
                })
            );

            return combineLatest([
                currentPeriodDataProvider$,
                previousPeriodDataProvider$
            ]).pipe(
                catchError((error) => {
                    this.zone.runOutsideAngular(() => {
                        data[index] = [];
                        previousData[index] = [];
                        erroredSeries[index] = true;
                        if (!erroredSeries.includes(false)) {
                            this.noData.emit(true);
                        }
                    });

                    return of(null);
                }),
                tap(() => {
                    if (data.every(x => !!x) && (!item.hasPreviousPeriod || previousData.every(x => !!x))) {
                        const combinedData = [item.hasPreviousPeriod ? previousData : [], data].flat();
                        const normalizedData = this.reportsSeriesDataGrouperService
                            ? this.reportsSeriesDataGrouperService.normalizeScale(combinedData, chart)
                            : combinedData;
                        const mergedData = this.reportsSeriesDataMergerService.mergeSeriesData(
                            normalizedData,
                            chart,
                            series
                        )[0];
                        this.chart.series.forEach((s, i) => {
                            if (s.calculateIndicators) {
                                s.calculateIndicators(mergedData, 'value' + s.indicatorTargetIndex, 'value' + i);
                            }
                        });
                        this.chartAnnotationIndicatorsService.calculateIndicatorsForGroupedData(this.chart);
                        chart.updateData(mergedData.slice());
                        if (chart.isContentEmpty && chart.isContentEmpty()) {
                            this.noContent.emit(true);
                        } else {
                            this.noContent.emit(false);
                        }
                    }
                })
            );
        });


        return combineLatest(series$);
    }

    setRenderedOutput(output: string): void {
        this.renderedOutputGenerated.emit(output);
    }
}
